import React from 'react';
import { Link } from 'react-router-dom';
import { useCountry } from '../../hooks/useCountry';

const PrivacyPolicy = () => {
  const { theme, isSubdomain } = useCountry();
  const colors = theme?.colors || {};

  return (
    <div className={isSubdomain ? '' : 'min-h-screen bg-gray-50'}>
      {/* Header - only on main domain */}
      {!isSubdomain && (
        <header className="bg-white shadow-sm">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <Link to="/" className="text-2xl font-bold text-blue-600 hover:text-blue-700">RentyDoc</Link>
          </div>
        </header>
      )}

      {/* Hero Banner */}
      <section className="text-white py-8 sm:py-12" style={{ background: theme ? colors.heroBg : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-3xl sm:text-4xl font-bold">Privacy Policy</h1>
          <p className="text-white/70 mt-2">Last updated: January 2025</p>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-5 sm:p-8 border border-white/50 shadow-sm prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 mb-4">
              RentyDoc ("we", "our", or "us") respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website and services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
            <h3 className="text-xl font-medium text-gray-800 mb-3">Personal Information</h3>
            <p className="text-gray-700 mb-4">When you use our Service, we may collect:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Name and contact information (email address, phone number)</li>
              <li>Account credentials (username, password - encrypted)</li>
              <li>Billing information (processed securely by Stripe)</li>
              <li>Contract data you provide (landlord/tenant details, property information)</li>
            </ul>
            <h3 className="text-xl font-medium text-gray-800 mb-3">Automatically Collected Information</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>IP address and browser type</li>
              <li>Device information</li>
              <li>Usage data and analytics</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700 mb-4">We use collected information to:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Provide and maintain our Service</li>
              <li>Generate your rental contract documents</li>
              <li>Process payments and send receipts</li>
              <li>Send you important updates about your account or documents</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>Improve our Service and develop new features</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Data Storage and Security</h2>
            <p className="text-gray-700 mb-4">We implement appropriate technical and organizational measures to protect your personal data:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>All data is encrypted in transit (HTTPS/TLS)</li>
              <li>Sensitive contract data is encrypted at rest</li>
              <li>Payment information is handled by Stripe (PCI-DSS compliant)</li>
              <li>Regular security audits and updates</li>
              <li>Access controls and authentication requirements</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Sharing and Disclosure</h2>
            <p className="text-gray-700 mb-4">We do not sell your personal information. We may share data with:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li><strong>Service Providers:</strong> Stripe for payment processing, hosting providers</li>
              <li><strong>Legal Requirements:</strong> When required by law or legal process</li>
              <li><strong>Business Transfers:</strong> In connection with a merger or acquisition</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Your Rights</h2>
            <p className="text-gray-700 mb-4">Depending on your location, you may have the right to:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Access and receive a copy of your personal data</li>
              <li>Correct inaccurate personal data</li>
              <li>Request deletion of your personal data</li>
              <li>Object to or restrict processing of your data</li>
              <li>Data portability</li>
              <li>Withdraw consent at any time</li>
            </ul>
            <p className="text-gray-700 mb-4">
              To exercise these rights, please contact us at <a href="mailto:privacy@rentydoc.com" className="hover:underline" style={{ color: colors.primary || '#2563eb' }}>privacy@rentydoc.com</a>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Cookies</h2>
            <p className="text-gray-700 mb-4">We use cookies and similar technologies to:</p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li>Keep you signed in</li>
              <li>Remember your preferences</li>
              <li>Understand how you use our Service</li>
              <li>Improve our Service</li>
            </ul>
            <p className="text-gray-700 mb-4">
              You can control cookies through your browser settings. Note that disabling cookies may affect Service functionality.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Data Retention</h2>
            <p className="text-gray-700 mb-4">
              We retain your personal data only as long as necessary to provide our Service and fulfill the purposes described in this policy. Contract data is retained for a reasonable period to allow you to access your documents and for legal compliance purposes.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. International Transfers</h2>
            <p className="text-gray-700 mb-4">
              Your data may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for such transfers in compliance with applicable data protection laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Children's Privacy</h2>
            <p className="text-gray-700 mb-4">
              Our Service is not intended for individuals under 18 years of age. We do not knowingly collect personal information from children.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Changes to This Policy</h2>
            <p className="text-gray-700 mb-4">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Contact Us</h2>
            <p className="text-gray-700 mb-4">
              For questions about this Privacy Policy or our data practices, contact us at:
            </p>
            <p className="text-gray-700">
              Email: <a href="mailto:privacy@rentydoc.com" className="hover:underline" style={{ color: colors.primary || '#2563eb' }}>privacy@rentydoc.com</a><br />
              Or visit our <Link to="/contact" className="hover:underline" style={{ color: colors.primary || '#2563eb' }}>Contact Page</Link>
            </p>
          </section>
        </div>
      </div>

      {/* Footer - only on main domain */}
      {!isSubdomain && (
        <footer className="bg-gray-800 text-white py-8 mt-12">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <p className="text-gray-400">&copy; 2025 RentyDoc. All rights reserved.</p>
            <div className="mt-4 space-x-4">
              <Link to="/terms" className="text-gray-400 hover:text-white">Terms</Link>
              <Link to="/privacy" className="text-gray-400 hover:text-white">Privacy</Link>
              <Link to="/about" className="text-gray-400 hover:text-white">About</Link>
              <Link to="/contact" className="text-gray-400 hover:text-white">Contact</Link>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default PrivacyPolicy;
